import React, { useContext, useEffect, useMemo, useState } from "react";
import "./styles/CharactersTable.scss";
import { ActionsMenu } from "../../../components/ActionsMenu/ActionsMenu";
import EditIcon from "../../../components/VectorIcons/EditIcon";
import PowerIcon from "../../../components/VectorIcons/PowerIcon";
import ImportanceMark from "../../../components/ImportanceMark/ImportanceMark";
import DetailedTelevisionIcon from "../../../components/VectorIcons/DetailedTelevisionIcon";
import SheetContext from "../../../context/sheetContext";
import AppContext from "../../../context/appContext";
import ConfirmationBox from "../../../components/ConfirmationBox/ConfirmationBox";
import ScriptBoxCaller from "../../../components/ScriptBoxCaller/ScriptBoxCaller";
import ScriptBoxDescription from "../../../components/ScriptBoxDescription/ScriptBoxDescription";
import ScriptBox from "../../../components/ScriptBox/ScriptBox";
import TelevisionIcon from "../../../components/VectorIcons/TelevisionIcon";
import TableLoading from "../../../components/TableLoading/TableLoading";
import CreateAndEditCharacter from "../CreateAndEditCharacter/CreateAndEditCharacter";
import AlertBox from "../../../components/AlertBox/AlertBox";
import { sortCharacters } from "./sortCharacters";
import { Box, Typography } from "@mui/material";

const CharactersTable = (props) => {
  const { havePermission } = props;

  const sheetContext = useContext(SheetContext);
  const appContext = useContext(AppContext);
  const [stateConfirmation, setStateConfirmation] = useState(false);
  const [stateCurrentCharacter, setStateCurrentCharacter] = useState(null);
  const [stateDetailsBox, setStateDetailsBox] = useState({
    open: false,
    currentItem: {
      names: "",
      activities: "",
      description: "",
    },
  });

  const { id } = props;
  const { getCharacters, characters } = sheetContext;

  useEffect(() => {
    if (id) {
      getCharacters(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const characterItems = characters?.result ?? [];

  const sortedCharacters = useMemo(() => {
    return [...characterItems].sort(sortCharacters);
  }, [characterItems]);

  if (characters?.loading) return <TableLoading />;

  if (characters && characters.error)
    return (
      <AlertBox
        hasCloseIcon={false}
        text={`Erro ao carregar personagens. Por favor, contacte o administrador.`}
        type="error"
        color="#ff9494"
      />
    );

  const actionCharacter = (character, action) => {
    setStateCurrentCharacter(character);

    if (action === "active") {
      setStateConfirmation(true);
      return;
    }

    appContext.setCurrentStepBox("editCharacter");
  };

  const handleDetailsItem = (character) => {
    const identifications = character?.identifications ?? [];
    const activities = character?.activities ?? [];

    const names = identifications
      .map((item) => {
        const fullName = `${item?.name ?? ""} ${item?.lastName ?? ""}`.trim();
        const nickName = item?.nickName?.trim();

        if (!fullName && !nickName) return "";
        return `${fullName}${nickName ? ` (${nickName})` : ""}`.trim();
      })
      .filter(Boolean)
      .join(" | ");

    setStateDetailsBox({
      open: true,
      currentItem: {
        names,
        activities: activities.filter(Boolean).join(", "),
        description: character?.description ?? "",
      },
    });
  };

  const closedDescription = () => {
    setStateDetailsBox((previousState) => ({
      ...previousState,
      open: false,
    }));
  };

  return (
    <Box
      className="charactersTable"
      sx={{
        fontFamily: '"Globotipo Text", sans-serif',
        "& .MuiTypography-root, & .MuiButtonBase-root": {
          fontFamily: "inherit",
        },
      }}
    >
      {!!characterItems.length ? (
        <>
          <Box className="charactersTableHeader">
            <Typography
              component="span"
              className="charactersItemHeader characterName"
            >
              Nome
            </Typography>
            <Typography
              component="span"
              className="charactersItemHeader talentNames"
            >
              Talento
            </Typography>
            <Typography
              component="span"
              className="charactersItemHeader characterRelevance"
            >
              Relevância
            </Typography>
            <Typography
              component="span"
              className="charactersItemHeader characterWeight"
            >
              Peso
            </Typography>
            <Typography
              component="span"
              className="charactersItemHeader characterDetails"
            >
              Atividades/Características
            </Typography>
            <Typography
              component="span"
              className="charactersItemHeader characterDescription"
            >
              Descrição/Observação
            </Typography>
            <Box className="charactersItemHeader characterOptions" />
          </Box>

          <Box className="charactersTableBody">
            {sortedCharacters.map((character, index) => {
              const characterKey = character.id || `character-${index}`;
              const phases = character?.phases ?? [];
              const validPhases = phases.filter((item) => !!item?.talent);

              return (
                <Box className="charactersListItems" key={characterKey}>
                  <Box
                    className={`charactersItem ${
                      !character.isActive ? "opacity" : ""
                    }`}
                  >
                    <Box className="containerCharactersItem">
                      <Box className="characterNames">
                        {(character?.identifications ?? []).map(
                          (identification, i) => {
                            const identificationKey = `${
                              identification?.name ?? ""
                            }-${identification?.lastName ?? ""}-${
                              identification?.nickName ?? ""
                            }-${i}`;

                            return (
                              <React.Fragment key={identificationKey}>
                                <Box className="characterName">
                                  {!!identification.name && (
                                    <Typography
                                      component="span"
                                      className="name"
                                    >
                                      {identification.name}{" "}
                                      {identification.lastName}
                                    </Typography>
                                  )}

                                  {!!identification.nickName && (
                                    <>
                                      <Typography
                                        component="span"
                                        className="nickName"
                                      >
                                        {identification.nickName.trim()}
                                      </Typography>
                                      {i + 1 <
                                      (character?.identifications ?? [])
                                        .length ? (
                                        <Box className="characterSeparator">
                                          |
                                        </Box>
                                      ) : null}
                                    </>
                                  )}
                                </Box>
                              </React.Fragment>
                            );
                          }
                        )}
                      </Box>
                    </Box>

                    <Box className="talentNames">
                      {!!validPhases.length
                        ? validPhases.map((talent, talentIndex) => {
                            return (
                              <Box
                                className="talentName"
                                key={`${talent.talent}-${talent.phase}-${talentIndex}`}
                              >
                                <Typography component="span">
                                  {!!talent && talent.talent}
                                </Typography>
                                <Typography component="span">
                                  {!!talent.phase
                                    ? ` (${talent.phase.trim()})`
                                    : ""}
                                </Typography>
                                {talentIndex + 1 < validPhases.length ? (
                                  <Box className="characterSeparator">|</Box>
                                ) : null}
                              </Box>
                            );
                          })
                        : "-"}
                    </Box>

                    <Box className="characterRelevance">
                      <ImportanceMark
                        level={
                          !!character.relevance
                            ? character.relevance.value
                            : "unclassified"
                        }
                      />
                      <Typography component="span" className="relevanceType">
                        {!!character.relevance && character.relevance.value}
                      </Typography>
                    </Box>

                    <Box className="characterWeight">
                      {!!character.weight ? character.weight : "-"}
                    </Box>

                    <Box className="characterDetails">
                      <Box className="detailsTags">
                        {(character?.activities ?? []).map((activitie, ind) => {
                          return (
                            <Typography
                              component="span"
                              className="detailTag"
                              key={ind}
                            >
                              {activitie}
                            </Typography>
                          );
                        })}
                      </Box>
                    </Box>

                    <Box className="characterDescription">
                      <Typography component="span">
                        {!!character.description
                          ? `${character.description.slice(0, 35).trim()}${
                              character.description.length >= 35 ? "... " : ""
                            }`
                          : "-"}

                        {!!character.description &&
                        character.description.length >= 35 ? (
                          <ScriptBoxCaller
                            callerText="mais"
                            event={() => handleDetailsItem(character)}
                          />
                        ) : (
                          ""
                        )}
                      </Typography>
                    </Box>

                    <Box className="characterOptions">
                      {havePermission && (
                        <ActionsMenu
                          menuOptions={[
                            ...(character.isActive
                              ? [
                                  {
                                    label: "Editar",
                                    icon: <EditIcon />,
                                    action: () =>
                                      actionCharacter(character, "edit"),
                                  },
                                ]
                              : []),
                            {
                              label: character.isActive
                                ? "Inativar"
                                : "Ativar",
                              icon: (
                                <PowerIcon
                                  color={
                                    character.isActive ? "#EC1045" : "#009C69"
                                  }
                                />
                              ),
                              action: () =>
                                actionCharacter(character, "active"),
                            },
                          ]}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </>
      ) : (
        <Box className="noCharactersCreated">
          <DetailedTelevisionIcon />
          <Typography component="span" className="noCharactersMessage">
            Nenhum personagem cadastrado.
          </Typography>
        </Box>
      )}

      {appContext.current.step === "editCharacter" && (
        <ScriptBox
          open={appContext.current.step === "editCharacter"}
          title="Editar Personagem"
          icon={<TelevisionIcon color="#FFF" />}
          removePadding={true}
          marginBottom={true}
        >
          <CreateAndEditCharacter
            action="edit"
            id={id}
            data={stateCurrentCharacter}
          />
        </ScriptBox>
      )}

      {stateConfirmation && (
        <ConfirmationBox
          open={stateConfirmation}
          closedBox={() => setStateConfirmation(false)}
          handleEvent={() =>
            stateCurrentCharacter &&
            sheetContext.toggleCharacter(
              stateCurrentCharacter.id,
              !stateCurrentCharacter.isActive,
              id
            )
          }
          title="Tem certeza?"
          textButton={`Sim, ${
            stateCurrentCharacter?.isActive ? "Inativar" : "Ativar"
          }`}
          text={`Este personagem será ${
            stateCurrentCharacter?.isActive ? "Inativado" : "Ativado"
          }!`}
          buttonColor={stateCurrentCharacter?.isActive ? "#EC1045" : "#009C69"}
        />
      )}

      {stateDetailsBox.open && (
        <ScriptBoxDescription
          open={stateDetailsBox.open}
          clear={() => closedDescription()}
          title={stateDetailsBox.currentItem.names}
          subTitle={stateDetailsBox.currentItem.activities}
        >
          <Typography component="p" className="boxParagraph">
            {stateDetailsBox.currentItem.description}
          </Typography>
        </ScriptBoxDescription>
      )}
    </Box>
  );
};

export default CharactersTable;
